import React, { useEffect, useState, useCallback } from 'react';
import { api, fileUrl } from '@/lib/api';
import { TextField, Section } from '@/admin/AdminUI';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';

// Gallery Events — event CRUD plus photo assignment.
//
// Events carry the year; photos hold only an event_id, so renaming or
// re-yearing an event is one edit and every photo follows. Assignment goes
// through the dedicated /admin/gallery/assign-event endpoint — ordinary
// photo edits can never touch it. Photos are loaded through the UNCAPPED
// picker pagination, never the 200-capped admin list, so bulk work always
// sees the whole collection.

export default function GalleryEventsTab() {
  // ---- Events state ----
  const [events, setEvents] = useState([]);
  const [untaggedVisible, setUntaggedVisible] = useState(0);
  const [untaggedTotal, setUntaggedTotal] = useState(0);
  const [editing, setEditing] = useState(null); // {id?, name, year, ref_count}
  const [saving, setSaving] = useState(false);
  // Persistent status line: {kind: 'ok'|'err', msg}. load() never clears
  // it; each mutation's success path does.
  const [status, setStatus] = useState(null);

  // ---- Photos state (assignment) ----
  const [photos, setPhotos] = useState(null); // null until loaded
  const [photoFilter, setPhotoFilter] = useState('untagged'); // 'all' | 'untagged' | event id
  const [selected, setSelected] = useState(() => new Set());
  const [targetEvent, setTargetEvent] = useState('');
  const [assigning, setAssigning] = useState(false);

  // Assignment stays LOCKED until events have genuinely loaded: with an
  // empty event list every photo would classify as "Untagged", and one
  // bulk action could mass-untag real assignments. eventsReady flips
  // true only on a successful events response.
  //
  // The two loads carry INDEPENDENT error flags. Each load clears only
  // its own flag, so a successful events request can never hide a failed
  // photos request (or vice versa) and Retry stays visible while either
  // has failed. Load errors live here, not in `status`, so retries never
  // erase unrelated mutation messages.
  const [eventsReady, setEventsReady] = useState(false);
  const [eventsFailed, setEventsFailed] = useState(false);
  const [photosFailed, setPhotosFailed] = useState(false);

  const loadEvents = useCallback(async () => {
    // Fail-closed: readiness is revoked the moment a load BEGINS, so a
    // later refresh failure after an earlier success locks assignment
    // immediately instead of leaving stale event data active.
    setEventsReady(false);
    try {
      const r = await api.get('/admin/gallery-events');
      // A 200 with a malformed body must count as a FAILURE. Coercing a
      // missing events array to [] would classify every photo as
      // "Untagged" and open the door to a mass mis-untag.
      if (!Array.isArray(r.data?.events)) throw new Error('malformed events response');
      setEvents(r.data.events);
      setUntaggedVisible(r.data?.untagged_visible ?? 0);
      setUntaggedTotal(r.data?.untagged_total ?? 0);
      setEventsFailed(false);
      setEventsReady(true);
    } catch (e) {
      setEventsFailed(true);
    }
  }, []);

  // Load EVERY photo via the picker cursor until has_more is false — no
  // page-count cutoff. A repeated cursor or an inconsistent response
  // (has_more with no cursor, or an empty page claiming more) reports an
  // error instead of quietly presenting a partial collection as complete.
  const loadPhotos = useCallback(async () => {
    try {
      const all = [];
      const seenCursors = new Set();
      let cursor = null;
      for (;;) {
        const params = { limit: 200 };
        if (cursor) params.cursor = cursor;
        const r = await api.get('/admin/gallery/picker', { params });
        const items = r.data?.items;
        if (!Array.isArray(items)) throw new Error('bad response');
        all.push(...items);
        if (!r.data?.has_more) break;
        const next = r.data?.next_cursor;
        if (typeof next !== 'string' || next === '' || items.length === 0 || seenCursors.has(next)) {
          throw new Error('inconsistent pagination');
        }
        seenCursors.add(next);
        cursor = next;
      }
      setPhotos(all);
      setPhotosFailed(false);
    } catch (e) {
      setPhotos(null);
      setPhotosFailed(true);
    }
  }, []);

  const retryLoads = useCallback(() => { loadEvents(); loadPhotos(); }, [loadEvents, loadPhotos]);

  useEffect(() => { loadEvents(); loadPhotos(); }, [loadEvents, loadPhotos]);

  // ---- Event CRUD ----
  const startAdd = () => setEditing({ name: '', year: new Date().getFullYear() });
  const startEdit = (ev) => setEditing({ ...ev });

  const saveEvent = async () => {
    const name = (editing.name || '').trim();
    const year = parseInt(editing.year, 10);
    if (!name) { setStatus({ kind: 'err', msg: 'An event name is required.' }); return; }
    if (!Number.isInteger(year) || year < 1000 || year > 9999) {
      setStatus({ kind: 'err', msg: 'Year must be a four-digit number.' }); return;
    }
    if (editing.id) {
      const original = events.find((e) => e.id === editing.id);
      if (original && original.year !== year) {
        const n = original.ref_count || 0;
        const okGo = window.confirm(`Move "${name}" to ${year}? ${n} photo${n === 1 ? '' : 's'} will follow it.`);
        if (!okGo) return;
      }
    }
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/admin/gallery-events/${editing.id}`, { name, year });
      } else {
        await api.post('/admin/gallery-events', { name, year });
      }
      setEditing(null);
      setStatus({ kind: 'ok', msg: editing.id ? 'Event updated.' : 'Event added.' });
      loadEvents();
    } catch (e) {
      setStatus({ kind: 'err', msg: e?.response?.data?.detail || 'Save failed. Nothing was changed — try again.' });
    } finally { setSaving(false); }
  };

  const removeEvent = async (ev) => {
    if ((ev.ref_count || 0) > 0) return; // button is disabled; belt and braces
    if (!window.confirm(`Delete "${ev.name}"?`)) return;
    try {
      await api.delete(`/admin/gallery-events/${ev.id}`);
      setStatus({ kind: 'ok', msg: 'Event deleted.' });
      loadEvents();
    } catch (e) {
      setStatus({ kind: 'err', msg: e?.response?.data?.detail || 'Delete failed. Nothing was changed — try again.' });
    }
  };

  const moveEvent = async (ev, dir) => {
    const yearGroup = events.filter((e) => e.year === ev.year);
    const idx = yearGroup.findIndex((e) => e.id === ev.id);
    const swap = idx + dir;
    if (swap < 0 || swap >= yearGroup.length) return;
    const next = [...yearGroup];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    try {
      await api.put('/admin/gallery-events/reorder', { items: next.map((e, i) => ({ id: e.id, order: i + 1 })) });
      setStatus({ kind: 'ok', msg: 'Order saved.' });
      loadEvents();
    } catch (e) {
      // The write may have partially applied server-side is impossible here
      // (fully-validated route writes nothing on rejection), but re-read
      // anyway so the screen always shows the saved order.
      setStatus({ kind: 'err', msg: e?.response?.data?.detail || 'Reorder failed.' });
      loadEvents();
    }
  };

  // ---- Assignment ----
  const eventById = {};
  for (const e of events) eventById[e.id] = e;

  const shownPhotos = !Array.isArray(photos) ? [] : photos.filter((p) => {
    const eid = p.event_id;
    const tagged = typeof eid === 'string' && eid !== '' && eventById[eid];
    if (photoFilter === 'all') return true;
    if (photoFilter === 'untagged') return !tagged;
    return eid === photoFilter;
  });

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllShown = () => setSelected(new Set(shownPhotos.map((p) => p.id)));
  const clearSelection = () => setSelected(new Set());

  const runAssign = async (eventIdOrNull) => {
    const ids = Array.from(selected);
    if (ids.length === 0) { setStatus({ kind: 'err', msg: 'Select at least one photo first.' }); return; }
    if (eventIdOrNull !== null && !eventById[eventIdOrNull]) {
      setStatus({ kind: 'err', msg: 'Choose an event to assign to.' }); return;
    }
    setAssigning(true);
    try {
      const r = await api.put('/admin/gallery/assign-event', { photo_ids: ids, event_id: eventIdOrNull });
      const n = r.data?.assigned ?? r.data?.untagged ?? ids.length;
      setStatus({
        kind: 'ok',
        msg: eventIdOrNull
          ? `Assigned ${n} photo${n === 1 ? '' : 's'} to "${eventById[eventIdOrNull].name}".`
          : `Untagged ${n} photo${n === 1 ? '' : 's'}.`,
      });
      clearSelection();
      loadPhotos();
      loadEvents();
    } catch (e) {
      setStatus({ kind: 'err', msg: e?.response?.data?.detail || 'Assignment failed. Nothing was changed — try again.' });
    } finally { setAssigning(false); }
  };

  // Group events by year (list arrives sorted year desc, order asc).
  const yearsInOrder = [];
  const byYear = {};
  for (const e of events) {
    if (!byYear[e.year]) { byYear[e.year] = []; yearsInOrder.push(e.year); }
    byYear[e.year].push(e);
  }

  return (
    <div>
      {/* Load errors are independent of mutation status: this banner stays
          up while EITHER load has failed, and a successful retry of one
          load can never hide the other's failure. */}
      {(eventsFailed || photosFailed) && (
        <p
          className="mb-4 font-body text-[12px] font-semibold rounded-sm px-3 py-2 border text-brand-red border-brand-red/40 bg-brand-red/5"
          data-testid="gallery-events-load-error"
        >
          {eventsFailed && photosFailed && 'Could not load events or photos.'}
          {eventsFailed && !photosFailed && 'Could not load events. Photo assignment is locked until this succeeds.'}
          {!eventsFailed && photosFailed && 'Could not load the complete photo list. Nothing is shown rather than a partial list.'}
          <button onClick={retryLoads} className="btn-secondary !py-1 !px-3 text-[11px] ml-3" data-testid="gallery-events-retry">
            Retry
          </button>
        </p>
      )}
      {status && (
        <p
          className={`mb-4 font-body text-[12px] font-semibold rounded-sm px-3 py-2 border ${status.kind === 'ok' ? 'text-ink-primary border-border-line bg-white' : 'text-brand-red border-brand-red/40 bg-brand-red/5'}`}
          data-testid="gallery-events-status"
        >
          {status.msg}
        </p>
      )}

      <Section
        title="Events"
        action={<button onClick={startAdd} className="btn-primary !py-2 !px-4 text-[12px]" data-testid="event-add"><Plus size={14} className="mr-1" /> Add Event</button>}
      >
        <p className="font-body text-[12px] text-ink-muted -mt-2">
          Each event belongs to one year. Photos assigned to an event appear under its year's filter on the public gallery. An event with no visible photos stays hidden from visitors. Untagged photos: <strong>{untaggedVisible}</strong> visible{untaggedTotal !== untaggedVisible ? ` (${untaggedTotal} total)` : ''}.
        </p>
        {yearsInOrder.length === 0 && (
          <p className="font-body text-[12px] text-ink-muted py-4">No events yet. Add the first one, then assign photos to it below.</p>
        )}
        {yearsInOrder.map((year) => (
          <div key={year} className="mt-2">
            <h3 className="font-display font-bold uppercase text-ink-primary text-[14px] tracking-tightish mb-2">{year}</h3>
            <div className="space-y-2">
              {byYear[year].map((ev, idx) => (
                <div key={ev.id} className="flex items-center gap-3 p-3 border border-border-line rounded-sm bg-white" data-testid="event-row">
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold uppercase text-ink-primary text-[13px] tracking-tightish break-words">{ev.name}</div>
                    <div className="font-body text-[11px] text-ink-muted">
                      {ev.visible_photo_count} visible photo{ev.visible_photo_count === 1 ? '' : 's'}{ev.ref_count !== ev.visible_photo_count ? ` (${ev.ref_count} total)` : ''}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => moveEvent(ev, -1)} disabled={idx === 0} className="p-1 disabled:opacity-30"><ChevronUp size={14} /></button>
                    <button onClick={() => moveEvent(ev, 1)} disabled={idx === byYear[year].length - 1} className="p-1 disabled:opacity-30"><ChevronDown size={14} /></button>
                  </div>
                  <button onClick={() => startEdit(ev)} className="btn-secondary !py-2 !px-3 text-[11px]">Edit</button>
                  <button
                    onClick={() => removeEvent(ev)}
                    disabled={(ev.ref_count || 0) > 0}
                    title={(ev.ref_count || 0) > 0 ? 'Untag or reassign its photos first' : 'Delete event'}
                    className="btn-secondary !py-2 !px-3 text-[11px] disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </Section>

      <Section title="Assign Photos">
        <p className="font-body text-[12px] text-ink-muted -mt-2">
          Tick photos, choose an event, then Assign. Untag removes the assignment. Ordinary edits elsewhere never change assignments.
        </p>
        {/* Nothing below renders until events have loaded: classifying
            photos against an empty event list would show everything as
            "Untagged" and invite a catastrophic bulk mis-untag. */}
        {!(eventsReady && !eventsFailed) && (
          <p className="font-body text-[12px] text-ink-muted py-4" data-testid="assign-waiting">
            {eventsFailed ? 'Photo assignment is locked because events failed to load. Use Retry above.' : 'Loading events…'}
          </p>
        )}
        {eventsReady && !eventsFailed && (
        <>
        <div className="flex flex-wrap items-center gap-2">
          <label className="font-body text-[12px] font-semibold uppercase text-ink-muted tracking-wider2">Show:</label>
          <select
            value={photoFilter}
            onChange={(e) => { setPhotoFilter(e.target.value); clearSelection(); }}
            className="border border-border-line rounded-sm px-2 py-2 font-body text-[13px] bg-white w-full sm:w-auto sm:max-w-[340px] min-w-0"
            data-testid="assign-filter"
          >
            <option value="untagged">Untagged only</option>
            <option value="all">All photos</option>
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>{ev.year} — {ev.name}</option>
            ))}
          </select>
          <span className="font-body text-[12px] text-ink-muted">{shownPhotos.length} shown · {selected.size} selected</span>
          <button onClick={selectAllShown} className="btn-secondary !py-2 !px-3 text-[11px]">Select all shown</button>
          <button onClick={clearSelection} className="btn-secondary !py-2 !px-3 text-[11px]">Clear selection</button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={targetEvent}
            onChange={(e) => setTargetEvent(e.target.value)}
            className="border border-border-line rounded-sm px-2 py-2 font-body text-[13px] bg-white w-full sm:w-auto sm:max-w-[340px] min-w-0"
            data-testid="assign-target"
          >
            <option value="">Choose event…</option>
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>{ev.year} — {ev.name}</option>
            ))}
          </select>
          <button
            onClick={() => runAssign(targetEvent || null)}
            disabled={assigning || selected.size === 0 || !targetEvent}
            className="btn-primary !py-2 !px-4 text-[12px] disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="assign-run"
          >
            {assigning ? 'Working…' : `Assign ${selected.size || ''}`}
          </button>
          <button
            onClick={() => runAssign(null)}
            disabled={assigning || selected.size === 0}
            className="btn-secondary !py-2 !px-4 text-[12px] disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="assign-untag"
          >
            Untag selected
          </button>
        </div>

        {photos === null && !photosFailed && (
          <p className="font-body text-[12px] text-ink-muted py-4">Loading photos…</p>
        )}
        {photos === null && photosFailed && (
          <p className="font-body text-[12px] text-brand-red py-4" data-testid="assign-photos-error">
            The photo list failed to load. Use Retry above — a partial list is never shown.
          </p>
        )}
        {Array.isArray(photos) && shownPhotos.length === 0 && (
          <p className="font-body text-[12px] text-ink-muted py-4">
            {photoFilter === 'untagged' ? 'Every photo is tagged. Nice work.' : 'No photos match this view.'}
          </p>
        )}
        {photos !== null && shownPhotos.length > 0 && (
          <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-2">
            {shownPhotos.map((p) => {
              const isSel = selected.has(p.id);
              const ev = typeof p.event_id === 'string' ? eventById[p.event_id] : null;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => toggleSelect(p.id)}
                  className={`relative rounded-sm overflow-hidden border-2 text-left ${isSel ? 'border-brand-orange' : 'border-transparent'}`}
                  data-testid="assign-photo"
                >
                  <div className="relative bg-bg-canvas" style={{ aspectRatio: '4/3' }}>
                    {p.image && (
                      <img
                        loading="lazy"
                        decoding="async"
                        src={fileUrl(p.image) || p.image}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    )}
                    {isSel && <div className="absolute inset-0 bg-brand-orange/25" aria-hidden="true" />}
                    <span className="absolute top-1 left-1 w-5 h-5 rounded-sm border border-white bg-black/50 flex items-center justify-center text-white text-[11px] font-bold">
                      {isSel ? '✓' : ''}
                    </span>
                  </div>
                  <div className="px-1 py-1 bg-white">
                    <span className={`block font-body text-[10px] truncate ${ev ? 'text-ink-primary' : 'text-ink-soft'}`}>
                      {ev ? `${ev.year} — ${ev.name}` : 'Untagged'}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
        </>
        )}
      </Section>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4" onClick={() => setEditing(null)}>
          <div className="bg-bg-elevated max-w-md w-full rounded-lg p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold uppercase text-[18px] mb-4">{editing.id ? 'Edit Event' : 'New Event'}</h3>
            <div className="space-y-4">
              <TextField
                label="Event name"
                value={editing.name}
                onChange={(v) => setEditing({ ...editing, name: v })}
                testid="event-edit-name"
              />
              <label className="block">
                <span className="block font-body text-[12px] font-semibold uppercase text-ink-muted tracking-wider2 mb-1.5">Year</span>
                <input
                  type="number"
                  value={editing.year ?? ''}
                  onChange={(e) => setEditing({ ...editing, year: e.target.value })}
                  className="w-full border border-border-line rounded-sm px-3 py-2 font-body text-[14px] bg-white focus:outline-none focus:border-ink-primary"
                  data-testid="event-edit-year"
                />
              </label>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="btn-secondary text-[12px] !py-2 !px-4">Cancel</button>
              <button onClick={saveEvent} disabled={saving} className="btn-primary text-[12px] !py-2 !px-4" data-testid="event-edit-save">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
